/**
 * @author Vibhor <vibhor.sharma@314ecorp.com>
 * @description Component for custom password field with validation
 */
import _ from 'lodash';
import { CheckOne, Dot } from '@icon-park/react';
import { Input, Flex, Typography, Card, Popover } from 'antd';
import { useEffect, useState } from 'react';

import { GREEN_HEX_CODE } from 'app-constants/eligibility';

export interface IProps {
	value: string;
	onChange: (value: string) => void;
}

interface IRequirement {
	isSatisfied: boolean;
	text: string;
}

const specialCharsText = `!@#$%^&*()-_=+[]{}|\\:;"'<>,.?/`;
const specialCharsRegex = new RegExp(`[${specialCharsText.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&')}]`);

const passwordRequirements = [
	{
		text: 'At least 8 characters',
		regexPattern: /.{8,}/,
	},
	{
		text: 'At least one lowercase letter',
		regexPattern: /[a-z]/,
	},
	{
		text: 'At least one uppercase letter',
		regexPattern: /[A-Z]/,
	},
	{
		text: 'At least one number',
		regexPattern: /\d/,
	},
	{
		text: `At least one special character (Allowed: ${specialCharsText})`,
		regexPattern: specialCharsRegex,
	},
];

const validateRequirements = (value: string): { items: IRequirement[]; error: string } => {
	const items = _.map(passwordRequirements, (requirement) => ({ ...requirement, isSatisfied: requirement.regexPattern.test(value) }));
	const errorText = _.find(items, { isSatisfied: false })?.text;
	return {
		items,
		error: errorText ? `Required : ${errorText}` : '',
	};
};

/**
 * Formily custom password field with validation requirements
 * @param {IProps} props props
 * @returns {React.FC} CustomPasswordValidator
 */
const CustomPasswordValidator: React.FC<IProps> = ({ value, onChange }) => {
	const [focused, setFocused] = useState<boolean>(false);
	const [requirements, setRequirements] = useState<IRequirement[]>(validateRequirements('').items);

	useEffect(() => {
		setRequirements(validateRequirements(value).items);
	}, [value]);

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		onChange(e.target.value);
	};

	const requirementsContent = (
		<Card
			styles={{ body: { backgroundColor: '#F5F5F5' } }}
			title={
				<Typography.Title level={4} style={{ margin: 0 }}>
					{'Password Requirements'}
				</Typography.Title>
			}
		>
			<Flex vertical gap={8}>
				{_.map(requirements, (requirement) => (
					<Flex key={requirement.text} align='center' gap={8}>
						{requirement.isSatisfied ? (
							<CheckOne theme='filled' fill={GREEN_HEX_CODE} />
						) : (
							<Dot theme='filled' fill='#2759CD' />
						)}
						<Typography.Text
							type={requirement.isSatisfied ? 'secondary' : null}
							style={{ textDecoration: requirement.isSatisfied ? 'line-through' : 'none' }}
						>
							{requirement.text}
						</Typography.Text>
					</Flex>
				))}
			</Flex>
		</Card>
	);

	return (
		<Popover
			content={requirementsContent}
			title={null}
			open={focused}
			placement='leftTop'
			styles={{ body: { padding: 0, marginRight: 12 } }}
			arrow={false}
		>
			<Input.Password
				value={value}
				onChange={handleChange}
				placeholder={'Set a strong password'}
				onFocus={() => setFocused(true)}
				onBlur={() => setFocused(false)}
			/>
		</Popover>
	);
};

export { validateRequirements };
export default CustomPasswordValidator;
